import type { EntryKind } from "../core/eventLog";

/** Formats a `performance.now()`-relative timestamp as `H:MM:SS.mmm` elapsed since page load. */
export function formatRelativeTime(tMs: number): string {
  const totalMs = Math.max(0, Math.round(tMs));
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const sec = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  const min = totalMin % 60;
  const hr = Math.floor(totalMin / 60);
  const pad = (n: number, len = 2): string => String(n).padStart(len, "0");
  const prefix = hr > 0 ? `${hr}:${pad(min)}:` : `${min}:`;
  return `${prefix}${pad(sec)}.${pad(ms, 3)}`;
}

/** CSS class suffix used to colour-code a badge for a given entry kind. */
export function kindBadgeClass(kind: EntryKind): string {
  return `badge-${kind}`;
}

/**
 * Serialises a value to JSON, tolerating circular references and BigInts without throwing.
 * Used anywhere an SDK payload of unknown shape must be displayed or copied.
 */
export function safeStringify(value: unknown, indent = 2): string {
  const ancestors: unknown[] = [];
  try {
    return JSON.stringify(
      value,
      function replacer(_key, val: unknown) {
        if (typeof val === "bigint") return `${val.toString()}n`;
        if (typeof val !== "object" || val === null) return val;
        while (
          ancestors.length > 0 &&
          ancestors[ancestors.length - 1] !== this
        ) {
          ancestors.pop();
        }
        if (ancestors.includes(val)) return "[Circular]";
        ancestors.push(val);
        return val;
      },
      indent,
    );
  } catch {
    return "[unserializable value]";
  }
}

/**
 * Drops SDK provider/proxy circular guts from a preview so the timeline shows
 * handshake fields instead of `{"_provider":{"proxy":"[Circular]"…`.
 */
function slimPreview(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const o = value as Record<string, unknown>;
  if (!("_provider" in o || "proxy" in o)) return value;
  const keep: Record<string, unknown> = {};
  for (const k of [
    "instanceId",
    "appInstanceId",
    "scope",
    "parameters",
    "launchedBy",
    "region",
    "config",
  ]) {
    if (Object.hasOwn(o, k)) keep[k] = o[k];
  }
  return keep;
}

/** A single-line, length-capped preview of a payload for dense list rows. */
export function truncatePreview(value: unknown, max = 140): string {
  if (value === undefined) return "";
  const flat = safeStringify(slimPreview(value), 0);
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}
